import type { AvatarCatalog } from '../../api/avatar-catalog';
import { mediaUrl } from './studio-content';
const artworkUrls = new Set<string>();
export function createArtworkUrl(blob: Blob) {
  const url = URL.createObjectURL(blob);
  artworkUrls.add(url);
  return url;
}
export function revokeArtworkUrl(url: string) {
  artworkUrls.delete(url);
  URL.revokeObjectURL(url);
}
export function artworkUrl(value: unknown): string | null {
  return typeof value === 'string' && artworkUrls.has(value) ? value : mediaUrl(value);
}

/** Декоративная витрина использует один публичный каталог, без запросов на каждую карточку. */
export function studioArtPhotos(catalog: AvatarCatalog | undefined, ids: unknown): string[] {
  const requested = Array.isArray(ids) && ids.length ? ids : catalog?.recommended_ids;
  const cards = requested?.map(id => catalog?.avatars.find(card => card.id === id)).filter(card => !!card);
  return (cards?.length ? cards : (catalog?.avatars ?? [])).flatMap(card => {
    const url = artworkUrl(card?.image_url);
    return url ? [url] : [];
  });
}

/** Сколько портретов покажет витрина, когда картинки придут: по самим данным (id кадра или строки, иначе
    рекомендованные), а не по пришедшим картинкам — тот же отбор, что у studioArtPhotos. Пока список не пришёл,
    считаются запрошенные id. Нужно, чтобы место под коллажем стояло с первого кадра (data-n строки тренда эталона
    — из данных, синхронно) */
export function studioArtCount(catalog: AvatarCatalog | undefined, ids: unknown): number {
  const requested = Array.isArray(ids) && ids.length ? ids : catalog?.recommended_ids;
  if (!catalog) return requested?.length ?? 0;
  const found = requested?.filter(id => catalog.avatars.some(card => card.id === id)).length ?? 0;
  return found || catalog.avatars.length;
}

/** Столбики волны, как wave(n, seed, step) прототипа: высота в процентах контейнера (а не в пикселях —
    у .st-wv своя высота в bot-studio.css и overflow: hidden), --d — сдвиг фазы анимации. */
export function studioWave(n: number, seed: number, step = 0): Array<{ height: string; delay: string }> {
  let state = seed;
  const next = () => {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
  return Array.from({ length: n }, (_, index) => ({
    height: `${Math.round(22 + next() * 78)}%`,
    delay: `${index * step}ms`,
  }));
}

/** Конический «орб» персонажа — те же цвета и формула, что у ORB и BotCore.orb прототипа */
const ORB: Record<string, [string, string]> = {
  '2104': ['#9fd3ff', '#2b5f8a'],
  '2101': ['#e4e0d6', '#6b6558'],
  '2106': ['#c4b5fd', '#4c1d95'],
  me: ['#6ee7b7', '#065f46'],
};
export function studioOrb(character: number | string = 'me'): string {
  const [from, to] = studioOrbColors(character);
  return `conic-gradient(from 210deg at 50% 50%,${from},${to},${from})`;
}
/** Пара цветов кружка: эталон кладёт их в data-c1/data-c2, потому что снимок кадра не читает
    градиенты CSS (orb() в engine.ts) */
export function studioOrbColors(character: number | string = 'me'): [string, string] {
  return (ORB[String(character)] ?? ORB.me) as [string, string];
}

/** Цветокоррекция плиток веера карточки «Все инструменты» (BotCore.app.GRADE) */
export const STUDIO_GRADE = [
  '',
  'saturate(1.5) contrast(1.06) hue-rotate(-14deg)',
  'grayscale(1) contrast(1.12)',
  'sepia(.42) saturate(1.25) brightness(1.04)',
  'saturate(1.2) hue-rotate(28deg) brightness(.95)',
];
